import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Input, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';
import { mdiMenuDown, mdiTrashCanOutline } from '@mdi/js';
import stores from 'cd-eft/models/Stores';
import Icon from '@mdi/react';
import './AlignmentTable.scss';
import { setAlignmentStationToDelete } from '../IndependentAlignment/IndependentAlignmentVM';
import Draggable from 'react-draggable';
import { ActionsMenu } from '../../common/ActionsMenu';

const { projectsLibraryStore: libraryStore, designStore, alignmentDesignStore: alignmentStore } = stores.AppStore;

interface AlignmentTableProps {
  currentObject: any;
  handleRowMouseEnter: (station: any) => void;
  handleRowMouseLeave: (station: any) => void;
  updateSingleAlignmentStation: (e: any) => void;
  updateMapAndProfile: () => void;
  handleCurveValueInputFocus: (e: any, index: number) => void;
  vm: any;
  enableAdvancedFeatures?: boolean;
}

export const AlignmentTable = observer(
  ({
    currentObject,
    handleRowMouseEnter,
    handleRowMouseLeave,
    updateSingleAlignmentStation,
    updateMapAndProfile,
    handleCurveValueInputFocus,
    vm,
    enableAdvancedFeatures = false
  }) => {
    const [isDropdownHovered, setIsDropdownHovered] = useState(false);
    const [stationForModal, setStationForModal] = useState<any>(null);
    const stationsCount = currentObject.alignmentStations.length - 1;

    const getElementActions = (station: any, index: number) => {
      // For TerraceDesignAlignmentView, just return delete action
      if (!enableAdvancedFeatures) {
        return {
          'Delete Station': { 
            action: (e) => setAlignmentStationToDelete(station.stationName, e)
          }
        };
      }
    
      // Advanced features for IndependentAlignment
      if (index === 0 || index === stationsCount) {
        return {};
      }
      const actions: any = {
        'Update Curve Value': {
          action: (e: Event) => {
            e.preventDefault();
            const initialValue = station.curveValue && station.curveValue > 0 
              ? station.curveValue 
              : undefined;
            
            vm.openCurveValueModal(initialValue);
            setStationForModal(station);
            alignmentStore.selectedAlignments.add(station.stationName);
            alignmentStore.selectedAlignments = new Set(alignmentStore.selectedAlignments);
          },
        },
        'Delete Station': { 
          action: (e) => setAlignmentStationToDelete(station.stationName, e),
        }
      };
      return actions;
    };

    return (
      <>
        <div className="alignment-table-container height-auto">
            <Table className="reach-table">
              <thead>
                <tr>
                  {enableAdvancedFeatures && (
                    <th 
                      className="narrow-td select-all-column"
                      onMouseEnter={() => setIsDropdownHovered(true)}
                      onMouseLeave={() => setIsDropdownHovered(false)}
                    >
                      <div className="text-center float-left d-flex align-items-center">
                        Select/<br/>Deselect All
                        <div className="dropdown">
                          <Icon path={mdiMenuDown} size={1.2} className="menu-down-icon" />
                          {isDropdownHovered && (
                            <div className="dropdown-content" id="dropdown-content">
                              <span 
                                role="button" 
                                tabIndex={0} 
                                onClick={(e) => {
                                  e.preventDefault();
                                  vm.selectAllStations();
                                }} 
                                className="select-all-text"
                              >
                                Select All
                              </span>
                              <span 
                                role="button" 
                                tabIndex={0} 
                                onClick={(e) => {
                                  e.preventDefault();
                                  vm.deselectAllStations();
                                }} 
                                className="select-all-text"
                              >
                                Deselect All
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </th>
                  )}
                  <th>Station</th>
                  <th>X</th>
                  <th>Y</th>
                  <th>Z</th>
                  <th>Curve Type</th>
                  <th>Curve Value</th>
                  <th>To PI Station</th>
                  <th className="action-th">Action</th>
                </tr>
              </thead>
              <tbody>
                {currentObject.alignmentStations &&
                  currentObject.alignmentStations.map((alignmentStation: any, i: number) => (
                    <tr
                      key={'key-alignmentStation-' + i}
                      id={'key-alignmentStation-' + i}
                      onMouseEnter={() => handleRowMouseEnter(alignmentStation)}
                      onMouseLeave={() => handleRowMouseLeave(alignmentStation)}
                      className={enableAdvancedFeatures && alignmentStore.selectedAlignments.has(alignmentStation.stationName) ? 'selected-row' : ''}
                    >
                      {enableAdvancedFeatures && (
                        <td className="narrow-td text-center">
                          {(
                            alignmentStation.stationName !== "START" && 
                            alignmentStation.stationName !== "END" && 
                            i !== 0 && 
                            i !== stationsCount
                          ) && (
                            <input
                              id={'select' + i}
                              data-testid={'select' + i}
                              name="select"
                              type="checkbox"
                              aria-label={`Select station ${i}`}
                              checked={alignmentStore.selectedAlignments.has(alignmentStation.stationName)}
                              value={i}
                              onChange={() => {
                                vm.handleCheckboxChange(alignmentStation);
                              }}
                            />
                          )}
                        </td>
                      )}
                      <td>
                        <input
                          className="usa-input"
                          name="stationName"
                        style={{ width: '70px' }}
                        aria-label={`stationName-${i}`}
                        data-testid={`stationName-${i}`}
                        type="number"
                        placeholder="0"
                        value={alignmentStation.stationName}
                        disabled
                      />
                    </td>
                    <td>
                      <input
                        className="usa-input"
                        style={{ width: '115px' }}
                        name="x"
                        aria-label={`x-${i}`}
                        data-testid={`x-${i}`}
                        data-i={i}
                        type="text"
                        value={Number(alignmentStation.x).toFixed(3)}
                        onChange={updateSingleAlignmentStation}
                        onBlur={updateMapAndProfile}
                        disabled={(i === 0 && i === stationsCount) || designStore.disableInput}
                      />
                    </td>
                    <td>
                      <input
                        className="usa-input"
                        style={{ width: '120px' }}
                        name="y"
                        width="fit-content"
                        aria-label={`y-${i}`}
                        data-testid={`y-${i}`}
                        data-i={i}
                        type="text"
                        value={Number(alignmentStation.y).toFixed(3)}
                        onChange={updateSingleAlignmentStation}
                        onBlur={updateMapAndProfile}
                        disabled={(i === 0 && i === stationsCount) || designStore.disableInput}
                      />
                    </td>
                    <td>
                      <input
                        className="usa-input"
                        style={{ width: '80px' }}
                        name="z"
                        aria-label={`z-${i}`}
                        data-testid={`z-${i}`}
                        type="text"
                        value={libraryStore.resetZValue ? '0.00' : Number(alignmentStation.z).toFixed(3)}
                        disabled
                      />
                    </td>
                    <td>
                      <select
                        className="usa-select m-0"
                        name="curveType"
                        aria-label="Curve Type"
                        style={{ width: '95px' }}
                        data-i={i}
                        data-testid={`curveType-${i}`}
                        value={+alignmentStation.curveType}
                        onChange={updateSingleAlignmentStation}
                        disabled={i === 0 || i === stationsCount}
                      >
                        <option value={0}>- Select -</option>
                        <option value={1}>NONE</option>
                        <option value={2}>RADIUS</option>
                      </select>
                    </td>
                    <td>
                      <input
                        className="usa-input"
                        style={{ width: '60px' }}
                        name="curveValue"
                        aria-label="Curve Value"
                        data-testid={`curveValue-${i}`}
                        data-i={i}
                        type="number"
                        min={0}
                        onChange={updateSingleAlignmentStation}
                        onFocus={(e) => handleCurveValueInputFocus(e, i)}
                        value={alignmentStation.curveValue}
                        disabled={i === 0 || i === stationsCount}
                      />
                    </td>
                    <td>
                      <input
                        className="usa-input"
                        style={{ width: '85px' }}
                        name="stationDistance"
                        aria-label={`stationDistance-${i}`}
                        data-testid={`stationDistance-${i}`}
                        type="number"
                        placeholder="0"
                        value={Number(alignmentStation.stationDistance).toFixed(2)}
                        disabled
                      />
                    </td>
                    <td className="text-center action-td">
                        {!enableAdvancedFeatures ? (
                          <button
                            data-testid={`action-menu-delete-${i}`}
                            className="usa-button usa-button--unstyled m-1"
                            onClick={(e) => setAlignmentStationToDelete(alignmentStation.stationName, e)}
                          >
                            <Icon path={mdiTrashCanOutline} title="Delete" size={1} />
                          </button>
                        ) : (
                          getElementActions(alignmentStation, i) && Object.keys(getElementActions(alignmentStation, i)).length > 0 && (
                            <ActionsMenu 
                              i={i} 
                              item={alignmentStation} 
                              elementActions={getElementActions(alignmentStation, i)}
                            />
                          )
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>
          {enableAdvancedFeatures && vm.showCurveValueModal && (
            <CurveValueModal vm={vm} stationToUpdate={stationForModal} />
          )}
      </>
    );
  }
);

export const CurveValueModal = observer(({ vm, stationToUpdate }: { vm: any, stationToUpdate?: any }) => {
  const placeholder = vm.curveValueModalState.curveValue === undefined 
    ? "Add curve value" 
    : "";
  
  return (
    <div className="modal-portal">
      <Draggable axis="both" positionOffset={{ x: '-50%', y: '-50%' }} cancel="input">
        <div className="curve-value-modal">
          <ModalHeader>
            <div className="header-content">Update Curve Value</div>
          </ModalHeader>
          <ModalBody>
            <div className="curve-value-modal-content">
              <div className="modal-row">
                <div>
                  <label htmlFor="curve_value">Enter a curve value number</label>
                  <Input
                    id="curve_value"
                    name="curve_value"
                    type="number"
                    value={vm.curveValueModalState.curveValue ?? ''}
                    step="0.01"
                    min="0"
                    placeholder={placeholder}
                    onChange={(e) => vm.handleCurveValueModalChange(
                      e.target.value === '' ? undefined : Number(e.target.value)
                    )}
                    className="usa-input"
                  />
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <div className="modal-buttons">
              <button 
                className="usa-button usa-button--outline close-button" 
                onClick={() => {
                  vm.handleCloseCurveValueModal();
                  if (stationToUpdate) {
                    alignmentStore.selectedAlignments.delete(stationToUpdate.stationName);
                    alignmentStore.selectedAlignments = new Set(alignmentStore.selectedAlignments);
                  }
                }}
              >
                Close
              </button>
              <button 
                className="usa-button save-button" 
                onClick={(e) => {
                  e.preventDefault();
                  if (stationToUpdate) {
                    vm.updateSpecificStationCurveValue(stationToUpdate, vm.curveValueModalState.curveValue);
                  } else {
                    vm.applyCurveValueToSelectedStations();
                  }
                  vm.handleCloseCurveValueModal();
                }}
                disabled={vm.curveValueModalState.curveValue === undefined || vm.curveValueModalState.curveValue < 0}
                >
                Apply
              </button>
            </div>
          </ModalFooter>
        </div>
      </Draggable>
    </div>
  );
});